import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import {
  getOptionsFromRefList,
  formatDropDownOptionsWithRefListId,
  isNonEmptyVariable,
  generateRules,
} from "@/util/common_function";
import { Form } from "antd";
import { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { useSelector } from "react-redux";

const PlaceBirthForm = forwardRef(
  ({
    formData,
    updateForm,
    bookingsFieldsEnabled,
    personalFormPlaceOfBirthRef,
    startingPoint,
  }, ref) => {
    const { isScheduledBooking } = useSelector((state) => state?.bookings);
    const [form] = Form.useForm();
    const [pobNcicOptions, setPobNcicOptions] = useState();
    const fullSearch = useSelector((state) => state.fullSearch);

    // -----------------------------------------------------------------------
    // Sync external formData → antd form
    // -----------------------------------------------------------------------
    useEffect(() => {
      form.setFieldsValue(formData);
    }, [formData]);

    // -----------------------------------------------------------------------
    // Expose form API to parent via ref
    // -----------------------------------------------------------------------
    useImperativeHandle(personalFormPlaceOfBirthRef, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: () => form.validateFields(),
    }));

    // -----------------------------------------------------------------------
    // POB Type change → fetch NCIC options
    // -----------------------------------------------------------------------
    const handlePobTypeChange = (value) => {
      if (!isNonEmptyVariable(value)) return;
      form.setFieldValue("pobNciCid", null);
      api.get(`${API_ENDPOINTS.destinationList}/${value}`).then(({ data }) => {
        setPobNcicOptions(formatDropDownOptionsWithRefListId(data));
      });
    };

    // Shared required rule builder — used by POB Type, POB NCIC, Country of Citizenship
    const bookingRequiredRule = (message) =>
      !isScheduledBooking && !fullSearch && startingPoint === "Bookings"
        ? [{ required: true, message }]
        : [];

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <FieldSecurityProvider
        entity="Bio"
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form
          form={form}
          disabled={!bookingsFieldsEnabled}
          initialValues={formData}
          onBlur={() => {
            const values = form.getFieldsValue();
            delete values?.dob;
            delete values?.lastModified;
            updateForm(values);
          }}
        >
          <StyledGridRow gutter={16}>

            {/* FBI */}
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="fbi"
                formItemProps={{
                  name: "fbi",
                  label: "FBI",
                  rules: [
                    {
                      pattern: /^[a-zA-Z0-9]{1,9}$/,
                      message: "The FBI number must be 9 alphanumeric characters or less.",
                    },
                  ],
                }}
              >
                <TextField 
                  className={startingPoint === "Warrants" ? "field--pink" : ""}
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* SID */}
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="sid"
                formItemProps={{
                  name: "sid",
                  label: "SID",
                  // Static fallback pattern plus whatever the client has configured
                  // under the Global Client Parameters "RegularExpression" /
                  // "Bio:SID" row (mirrors ASP's DataImporterInterface.asp
                  // ValidateRegularExpression for Bio:SID) — both are enforced.
                  rules: [
                    {
                      pattern: /^[a-zA-Z0-9]{1,10}$/,
                      message:
                        "The State ID (SID) field allows alphanumeric characters only and must be 10 characters or less.",
                    },
                    ...generateRules("Bio:SID", "SID", false),
                  ],
                }}
              >
                <TextField className={startingPoint === "Warrants" ? "field--pink" : ""} />
              </SecureFormItem>
            </StyledGridCol>

            {/* POB Type */}
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="pobType"
                formItemProps={{
                  name: "pobType",
                  label: "POB Type",
                  required: !fullSearch && startingPoint!="Warrants",
                  rules: bookingRequiredRule("POB Type is required"),
                }}
              >
                <Select
                  options={getOptionsFromRefList("POBType", false, false, true)}
                  placeholder="POB Type"
                  showSearch
                  variant="outlined"
                  onChange={handlePobTypeChange}
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* POB NCIC */}
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="pobNciCid"
                formItemProps={{
                  name: "pobNciCid",
                  label: "POB NCIC",
                  required: !fullSearch && startingPoint!="Warrants",
                  rules: bookingRequiredRule("POB NCIC is required"),
                }}
              >
                <Select
                  options={
                    Array.isArray(pobNcicOptions)
                      ? pobNcicOptions
                      : getOptionsFromRefList("POBNCIC", false, false, true)
                  }
                  placeholder="POB NCIC"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Country of Citizenship */}
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="countryOfCitizenshipId"
                formItemProps={{
                  name: "countryOfCitizenshipId",
                  label: "Country of Citizenship",
                  required: !fullSearch && startingPoint!="Warrants",
                  rules: bookingRequiredRule("Country of Citizenship is required"),
                }}
              >
                <Select
                  options={getOptionsFromRefList("POBNCIC", false, false, true)}
                  placeholder="Country of Citizenship"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Primary Language */}
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="primaryLanguage"
                formItemProps={{ name: "primaryLanguage", label: "Primary Language" }}
              >
                <Select
                  options={getOptionsFromRefList("LanguageList")}
                  placeholder="Primary Language"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>
    );
  }
);

export default PlaceBirthForm;